/**
 * VOY-73: Booking Status Card Component
 * 
 * Displays booking status with appropriate icon, color, and messaging
 */

import { CheckCircle2, XCircle, AlertCircle, Loader2, Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getStatusInfo, type BookingStatus } from '@/constants/ratehawkStatusCodes'
import { Link } from 'react-router-dom'

interface BookingStatusCardProps {
  status: BookingStatus | string | null | undefined
  orderId?: string
  onRetry?: () => void
  className?: string
}

const iconMap = {
  success: CheckCircle2,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
  loading: Loader2
}

const colorClasses = {
  green: 'border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800',
  yellow: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800',
  red: 'border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800',
  blue: 'border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800',
  gray: 'border-gray-200 bg-gray-50 dark:bg-gray-950 dark:border-gray-800'
}

const textColorClasses = {
  green: 'text-green-800 dark:text-green-200',
  yellow: 'text-yellow-800 dark:text-yellow-200',
  red: 'text-red-800 dark:text-red-200',
  blue: 'text-blue-800 dark:text-blue-200',
  gray: 'text-gray-800 dark:text-gray-200'
}

const iconColorClasses = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
  blue: 'text-blue-600 dark:text-blue-400',
  gray: 'text-gray-600 dark:text-gray-400'
}

export function BookingStatusCard({ 
  status, 
  orderId, 
  onRetry,
  className = '' 
}: BookingStatusCardProps) {
  const statusInfo = getStatusInfo(status)
  const Icon = iconMap[statusInfo.icon]
  const isAnimating = statusInfo.icon === 'loading'

  return (
    <Card className={`${colorClasses[statusInfo.color]} ${className}`}>
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          {isAnimating ? (
            <Icon className={`h-16 w-16 ${iconColorClasses[statusInfo.color]} animate-spin`} />
          ) : (
            <Icon className={`h-16 w-16 ${iconColorClasses[statusInfo.color]}`} />
          )}
        </div>
        <CardTitle className={`text-2xl ${textColorClasses[statusInfo.color]}`}>
          {statusInfo.label}
        </CardTitle>
        <p className={`${textColorClasses[statusInfo.color]} mt-2 opacity-90`}>
          {statusInfo.message}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className={`text-sm ${textColorClasses[statusInfo.color]} opacity-80`}>
          {statusInfo.description}
        </p>

        {orderId && (
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Order ID:</span>
              <span className="font-mono text-xs">{orderId}</span>
            </div>
          </div>
        )}

        {statusInfo.nextSteps.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-2">
            <h4 className={`font-semibold text-sm ${textColorClasses[statusInfo.color]}`}>
              What's Next?
            </h4>
            <ul className="space-y-1.5">
              {statusInfo.nextSteps.map((step, index) => (
                <li key={index} className={`text-sm ${textColorClasses[statusInfo.color]} opacity-80 flex items-start gap-2`}>
                  <span className="mt-1">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          {statusInfo.canRetry && onRetry && (
            <Button 
              onClick={onRetry}
              variant="outline"
              className="flex-1"
            >
              Try Again
            </Button>
          )}
          {statusInfo.supportContact && (
            <Button 
              asChild
              variant="outline"
              className="flex-1"
            >
              <Link to="/support">
                Contact Support
              </Link>
            </Button>
          )}
          {!statusInfo.canRetry && !statusInfo.supportContact && (
            <Button 
              asChild
              className="flex-1"
            >
              <Link to="/">
                Continue Shopping
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

